import { DialogComponent, DialogOpenEvent } from '@theme/dialog';

const CART_SECTION_RENDERED_EVENT = 'cart:section-rendered';

/**
 * A custom element that manages a cart drawer.
 * Auto-open waits for the drawer cart section to finish rendering, so the
 * customer never sees the drawer while its contents are still being morphed.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #autoOpenScheduled = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CART_SECTION_RENDERED_EVENT, this.#handleRenderedCart);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CART_SECTION_RENDERED_EVENT, this.#handleRenderedCart);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.#autoOpenScheduled = false;
  }

  /**
   * Opens only after the cart section used by this drawer has completed its
   * Shopify section render. This removes the previous open-during-morph race.
   * @param {Event} event
   */
  #handleRenderedCart = (event) => {
    if (!this.hasAttribute('auto-open') || this.#autoOpenScheduled) return;

    const detail = /** @type {any} */ (event).detail || {};
    if (detail.didError) return;

    const source = String(detail.source || '');
    const isNativeProductAdd = source === 'product-form-component';
    const isValleansAdd = source.startsWith('vh-') && source.includes('bundle');
    const explicitAdd = detail.action === 'add';

    if (!isNativeProductAdd && !isValleansAdd && !explicitAdd) return;

    const drawerCartItems = this.querySelector('cart-items-component[data-section-id]');
    const drawerSectionId = drawerCartItems instanceof HTMLElement ? String(drawerCartItems.dataset.sectionId || '') : '';
    const renderedSectionId = String(detail.sectionId || '');

    if (drawerSectionId && renderedSectionId && drawerSectionId !== renderedSectionId) return;

    this.#autoOpenScheduled = true;
    requestAnimationFrame(() => {
      this.#autoOpenScheduled = false;

      const currentDrawer = document.querySelector('cart-drawer-component[auto-open]');
      if (!(currentDrawer instanceof CartDrawerComponent) || !currentDrawer.isConnected) return;

      currentDrawer.open();
    });
  };

  open() {
    this.showDialog();

    /** Close cart drawer when installments CTA is clicked to avoid overlapping dialogs. */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = this.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = drawerHeight > 0 ? summaryHeight / drawerHeight : 0;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
